import React, { useState } from 'react';
import { Row, Col, Card, Timeline, Typography, Tag, Button, Tabs, Space } from 'antd';
import { CodeOutlined, LinkOutlined, RocketOutlined, HeartOutlined } from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;
const { TabPane } = Tabs;

const Experience = () => {
  const [activeTab, setActiveTab] = useState('work');

  const workExperience = [
    {
      title: 'Chief Software Engineer',
      company: 'Umotrack - Healthcare Logistics Platform',
      period: '2020 - Present',
      location: 'Sierra Leone & Uganda (Remote)',
      description: 'Leading development of mission-critical healthcare logistics platform for medical sample and supply transportation across multiple African countries, sponsored by Global Fund.',
      achievements: [
        'Successfully delivered 3,000,000+ medical samples across Sierra Leone and Uganda',
        'Led development team building scalable microservices architecture supporting multi-country operations',
        'Maintained 99.9% uptime for critical healthcare logistics operations',
        'Implemented real-time tracking system reducing delivery time by 40%',
        'Designed mobile-first interface for field workers improving data collection accuracy by 60%',
        'Built compliance and regulatory reporting system meeting international healthcare standards',
        'Expanded platform operations to multiple countries with robust, scalable architecture'
      ],
      technologies: ['React', 'Node.js', 'MongoDB', 'Express.js', 'Redis', 'Docker', 'AWS', 'Microservices', 'REST APIs'],
      website: 'https://sl-tracker.umojaimcouriers.com/',
      icon: <HeartOutlined />
    },
    {
      title: 'Co-founder & CTO',
      company: 'PI Technologies Uganda Ltd',
      period: '2019 - Present',
      location: 'Kampala, Uganda',
      description: 'Co-founded PI Technologies, a leading fintech company specializing in SACCO and microfinance management systems. Developed multitenant applications serving over 1,000,000 Ugandans across multiple financial institutions.',
      achievements: [
        'Co-founded fintech company serving over 1,000,000 Ugandans across multiple financial institutions',
        'Developed PiSacco Management System - specialized software for SACCO institutions supporting VSLA',
        'Built multitenant SaaS architecture serving 1+ billion UGX in processed transactions',
        'Created comprehensive microfinance management system for investment clubs and money lending institutions',
        'Implemented field collections app and bulk collections/payouts payment solutions',
        'Established 24/7 technical support and managed IT services for financial institutions'
      ],
      technologies: ['PHP', 'Laravel', 'MySQL', 'JavaScript', 'React', 'Node.js', 'AWS', 'Multitenant Architecture', 'REST APIs'],
      website: 'https://pitech.co.ug',
      icon: <CodeOutlined />
    },
    {
      title: 'Co-founder & CTO',
      company: 'AnidasoHub, Inc',
      period: '2024 - Present',
      location: 'United States',
      description: 'Co-founded AnidasoHub, Inc, a startup developing AI-powered voice agents for clinical trials. Leading technical development of innovative healthcare AI solutions.',
      achievements: [
        'Co-founded startup focused on AI-powered healthcare solutions for clinical trials',
        'Developed innovative voice agent technology using natural language processing',
        'Built scalable platform for patient recruitment and engagement in clinical research',
        'Implemented HIPAA-compliant data processing and storage systems',
        'Created user-friendly interface for trial coordinators and researchers',
        'Established technical architecture for global deployment and scalability'
      ],
      technologies: ['AI/ML', 'Natural Language Processing', 'Python', 'React', 'Node.js', 'AWS', 'Docker', 'Machine Learning'],
      website: 'https://anidasohub.com',
      icon: <RocketOutlined />
    },
    {
      title: 'Software Engineer & IT Manager',
      company: 'Canon Innovations Ltd',
      period: 'February 2019 - Present',
      location: 'New York, NY',
      description: 'Leading development of enterprise applications and managing IT infrastructure for scalable solutions.',
      achievements: [
        'Deployed financial systems across 8 branches serving 2,000+ members with 95% adoption rate within 3 months',
        'Implemented security protocols ensuring 100% compliance with financial standards and zero breach incidents',
        'Trained 50+ staff members on system usage, reducing support tickets by 70%',
        'Developed on-demand service platform serving 1,000+ daily users with 4.8-star rating',
        'Built school management system supporting 25+ institutions and 5,000+ students, reducing admin costs by 60%',
        'Managed IT infrastructure including server deployment, network configuration, and maintenance'
      ],
      technologies: ['Java', 'Python', 'Django', 'PostgreSQL', 'AWS', 'Linux', 'Docker'],
      website: 'https://canoninnovations.com/',
      icon: <CodeOutlined />
    },
    {
      title: 'Full-Stack Developer (Contract)',
      company: 'Pamoja Group Ltd',
      period: '2019',
      location: 'New York, NY',
      description: 'Delivered comprehensive business solutions including invoicing systems and e-commerce applications.',
      achievements: [
        'Delivered invoicing system processing 10,000+ monthly transactions with 99.5% accuracy',
        'Developed e-commerce POS app increasing sales processing speed by 50%',
        'Built salon management platform serving 15+ businesses, improving scheduling efficiency by 40%'
      ],
      technologies: ['PHP', 'Laravel', 'JavaScript', 'MySQL', 'Bootstrap', 'AJAX'],
      website: 'https://pamojagroupltd.com/',
      icon: <CodeOutlined />
    }
  ];

  const technicalProjects = [
    {
      title: 'Financial Management System',
      description: 'Comprehensive banking solution with core banking, e-wallets, and loan automation features.',
      features: [
        'Multi-branch support with 2,000+ active users',
        'Real-time transaction processing',
        'Advanced security protocols with 100% compliance',
        'Automated loan processing and approval system',
        'E-wallet integration with mobile payments'
      ],
      technologies: ['Java', 'Spring Boot', 'PostgreSQL', 'React', 'AWS'],
      achievements: [
        'Processed $15M+ in transactions with zero security incidents',
        'Achieved 95% user adoption rate within 3 months',
        'Reduced transaction processing time by 70%'
      ]
    },
    {
      title: 'School Management System',
      description: 'Comprehensive educational platform supporting 25+ institutions and 5,000+ students.',
      features: [
        'Student enrollment and academic record management',
        'Fee payment and financial tracking',
        'Teacher and staff management portal',
        'Parent communication system',
        'Academic reporting and analytics'
      ],
      technologies: ['PHP', 'Laravel', 'MySQL', 'Bootstrap', 'JavaScript'],
      achievements: [
        'Served 25+ educational institutions',
        'Reduced administrative costs by 60%',
        'Improved parent engagement by 80%'
      ]
    },
    {
      title: 'E-Commerce POS Application',
      description: 'Mobile-first point-of-sale system for retail businesses.',
      features: [
        'Inventory management and tracking',
        'Sales processing and receipt generation',
        'Customer management and loyalty programs',
        'Analytics and reporting dashboard',
        'Multi-location support'
      ],
      technologies: ['React Native', 'Node.js', 'MongoDB', 'Stripe API'],
      achievements: [
        'Increased sales processing speed by 50%',
        'Served 15+ businesses with 4.8-star rating',
        'Reduced inventory management errors by 75%'
      ]
    }
  ];

  const keyAchievements = [
    {
      metric: '3M+',
      label: 'Medical Samples Delivered',
      description: 'Through Umotrack platform across Sierra Leone and Uganda'
    },
    {
      metric: '500+',
      label: 'Businesses Served',
      description: 'Across various industries and sectors'
    },
    {
      metric: '99.9%',
      label: 'System Uptime',
      description: 'For critical healthcare logistics operations'
    },
    {
      metric: '$15M+',
      label: 'Transactions Processed',
      description: 'With zero security incidents'
    },
    {
      metric: '200+',
      label: 'Users Trained',
      description: 'Across various platforms with 95% adoption rate'
    }
  ];

  return (
    <section id="experience" className="section section-gray">
      <div className="container">
        <div className="section-header">
          <Title level={2} className="section-title">Professional Experience</Title>
          <Paragraph className="section-subtitle">My career journey and key achievements</Paragraph>
        </div>

        <Tabs activeKey={activeTab} onChange={setActiveTab} centered>
          <TabPane tab="Work Experience" key="work">
            <Row gutter={[32, 32]}>
              {workExperience.map((job, index) => (
                <Col key={index} xs={24}>
                  <Card
                    hoverable
                    style={{ 
                      marginBottom: '24px',
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    <Row gutter={[24, 24]} align="middle">
                      <Col xs={24} md={18}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                          <div style={{ 
                            fontSize: '24px', 
                            marginRight: '12px',
                            color: '#1890ff'
                          }}>
                            {job.icon}
                          </div>
                          <div>
                            <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
                              {job.title}
                            </Title>
                            <Text strong style={{ fontSize: '16px' }}>
                              {job.company}
                            </Text>
                          </div>
                        </div>
                        
                        <div style={{ marginBottom: '16px' }}>
                          <Space wrap>
                            <Tag color="blue">{job.period}</Tag>
                            <Tag color="green">{job.location}</Tag>
                          </Space>
                        </div>

                        <Paragraph style={{ marginBottom: '16px' }}>
                          {job.description}
                        </Paragraph>

                        <div style={{ marginBottom: '16px' }}>
                          <Title level={5} style={{ marginBottom: '8px' }}>Key Achievements:</Title>
                          <ul style={{ paddingLeft: '20px' }}>
                            {job.achievements.map((achievement, idx) => (
                              <li key={idx} style={{ marginBottom: '4px' }}>
                                {achievement}
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                          <Title level={5} style={{ marginBottom: '8px' }}>Technologies:</Title>
                          <div>
                            {job.technologies.map((tech, idx) => (
                              <Tag key={idx} size="small" color="blue" style={{ marginBottom: '4px' }}>
                                {tech}
                              </Tag>
                            ))}
                          </div>
                        </div>
                      </Col>
                      
                      <Col xs={24} md={6}>
                        <div style={{ textAlign: 'center' }}>
                          <Button
                            type="primary"
                            size="large"
                            icon={<LinkOutlined />}
                            href={job.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ 
                              width: '100%',
                              height: '40px',
                              marginBottom: '12px'
                            }}
                          >
                            Visit Website
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              ))}
            </Row>
          </TabPane>

          <TabPane tab="Technical Projects" key="projects">
            <Row gutter={[24, 24]}>
              {technicalProjects.map((project, index) => (
                <Col key={index} xs={24} md={12}>
                  <Card
                    hoverable
                    style={{ 
                      height: '100%',
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    <Title level={4} style={{ marginBottom: '12px', color: '#1890ff' }}>
                      {project.title}
                    </Title>
                    
                    <Paragraph style={{ marginBottom: '16px' }}>
                      {project.description}
                    </Paragraph>

                    <div style={{ marginBottom: '16px' }}>
                      <Title level={5} style={{ marginBottom: '8px' }}>Features:</Title>
                      <ul style={{ paddingLeft: '20px' }}>
                        {project.features.map((feature, idx) => (
                          <li key={idx} style={{ marginBottom: '4px', fontSize: '14px' }}>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <Title level={5} style={{ marginBottom: '8px' }}>Technologies:</Title>
                      <div>
                        {project.technologies.map((tech, idx) => (
                          <Tag key={idx} size="small" color="blue" style={{ marginBottom: '4px' }}>
                            {tech}
                          </Tag>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Title level={5} style={{ marginBottom: '8px' }}>Achievements:</Title>
                      <ul style={{ paddingLeft: '20px' }}>
                        {project.achievements.map((achievement, idx) => (
                          <li key={idx} style={{ marginBottom: '4px', fontSize: '14px' }}>
                            {achievement}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </TabPane>

          <TabPane tab="Key Achievements" key="achievements">
            <Row gutter={[24, 24]} justify="center">
              {keyAchievements.map((achievement, index) => (
                <Col key={index} xs={24} sm={12} md={8} lg={6}>
                  <Card
                    style={{ 
                      textAlign: 'center',
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    <Title level={2} style={{ color: '#1890ff', marginBottom: '8px' }}>
                      {achievement.metric}
                    </Title>
                    <Title level={4} style={{ marginBottom: '8px' }}>
                      {achievement.label}
                    </Title>
                    <Paragraph style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                      {achievement.description}
                    </Paragraph>
                  </Card>
                </Col>
              ))}
            </Row>
          </TabPane>
        </Tabs>
      </div>
    </section>
  );
};

export default Experience;